import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Member } from '@/types/member';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, Search, Gift } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getDisplayName } from '@/lib/userDisplayUtils';

interface SuggestProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  suggestedMember: Member;
  allMembers: Member[];
}

export const SuggestProfileDialog = ({ 
  isOpen, 
  onClose, 
  suggestedMember,
  allMembers 
}: SuggestProfileDialogProps) => {
  const { toast } = useToast();
  const [selectedRecipient, setSelectedRecipient] = useState<Member | null>(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrer les membres pour la sélection
  const filteredMembers = allMembers.filter(m => 
    m.id !== suggestedMember.id && // Pas lui-même
    (getDisplayName(m).toLowerCase().includes(searchTerm.toLowerCase()) ||
     m.company?.toLowerCase().includes(searchTerm.toLowerCase()))
  ).slice(0, 10);

  // Générer un message pré-rempli intelligent
  useEffect(() => {
    if (selectedRecipient && suggestedMember) {
      const matches: string[] = [];
      
      // Matcher needs du destinataire avec offers du membre suggéré
      if (selectedRecipient.needs && selectedRecipient.needs.length > 0 && 
          suggestedMember.offers && suggestedMember.offers.length > 0) {
        const needsMatch = selectedRecipient.needs.filter(need =>
          suggestedMember.offers?.some(offer => 
            offer.toLowerCase().includes(need.toLowerCase()) ||
            need.toLowerCase().includes(offer.toLowerCase())
          )
        );
        if (needsMatch.length > 0) {
          matches.push(`Tu recherches ${needsMatch.join(', ')} et ${getDisplayName(suggestedMember)} propose exactement ça`);
        }
      }

      // Matcher offers du destinataire avec needs du membre suggéré
      if (selectedRecipient.offers && selectedRecipient.offers.length > 0 && 
          suggestedMember.needs && suggestedMember.needs.length > 0) {
        const offersMatch = selectedRecipient.offers.filter(offer =>
          suggestedMember.needs?.some(need => 
            need.toLowerCase().includes(offer.toLowerCase()) ||
            offer.toLowerCase().includes(need.toLowerCase())
          )
        );
        if (offersMatch.length > 0) {
          matches.push(`${getDisplayName(suggestedMember)} recherche ${offersMatch.join(', ')} que tu proposes`);
        }
      }

      const defaultMessage = matches.length > 0
        ? `Salut ${selectedRecipient.firstname || getDisplayName(selectedRecipient)} 👋

Je pense que tu devrais absolument rencontrer ${getDisplayName(suggestedMember)}${suggestedMember.company ? ' de ' + suggestedMember.company : ''} !

✨ Pourquoi ? ${matches[0]}.

C'est exactement le genre de connexion qui peut créer de la valeur pour vous deux. N'hésite pas à le/la contacter ! 🚀`
        : `Salut ${selectedRecipient.firstname || getDisplayName(selectedRecipient)} 👋

Je pense que tu devrais rencontrer ${getDisplayName(suggestedMember)}${suggestedMember.company ? ' de ' + suggestedMember.company : ''}.

Vous avez des intérêts complémentaires et je suis sûr(e) qu'une discussion entre vous serait très enrichissante ! 🚀`;

      setMessage(defaultMessage);
    }
  }, [selectedRecipient, suggestedMember]);

  const handleSuggest = async () => {
    if (!selectedRecipient) {
      toast({
        title: "Destinataire manquant",
        description: "Veuillez sélectionner un destinataire",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Identifier les critères matchés
      const matchedCriteria: any = {};
      if (selectedRecipient.needs && suggestedMember.offers) {
        const needsMatched = selectedRecipient.needs.filter(need =>
          suggestedMember.offers?.some(offer => 
            offer.toLowerCase().includes(need.toLowerCase())
          )
        );
        if (needsMatched.length > 0) {
          matchedCriteria.needs_matched = needsMatched;
        }
      }
      if (selectedRecipient.offers && suggestedMember.needs) {
        const offersMatched = selectedRecipient.offers.filter(offer =>
          suggestedMember.needs?.some(need => 
            need.toLowerCase().includes(offer.toLowerCase())
          )
        );
        if (offersMatched.length > 0) {
          matchedCriteria.offers_matched = offersMatched;
        }
      }

      // Insérer la suggestion
      const { error: insertError } = await supabase
        .from('profile_suggestions')
        .insert({
          suggester_id: user.id,
          suggested_member_id: suggestedMember.id,
          recipient_id: selectedRecipient.user_id,
          message,
          context: Object.keys(matchedCriteria).length > 0 ? 'needs_match' : 'manual',
          matched_criteria: matchedCriteria
        });

      if (insertError) throw insertError;

      // Envoyer une notification interne
      const { error: notifError } = await supabase
        .from('notifications')
        .insert({
          user_id: selectedRecipient.user_id,
          type: 'profile_suggestion',
          title: `${getDisplayName({ firstname: user.user_metadata?.first_name, lastname: user.user_metadata?.last_name, email: user.email })} vous suggère un profil`,
          message: `${getDisplayName({ firstname: user.user_metadata?.first_name, lastname: user.user_metadata?.last_name, email: user.email })} pense que vous devriez rencontrer ${getDisplayName(suggestedMember)}`,
          data: {
            action_url: `/members?highlight=${suggestedMember.id}`,
            suggester_id: user.id,
            suggested_member_id: suggestedMember.id,
            matched_criteria: matchedCriteria
          }
        });

      toast({
        title: "✅ Suggestion envoyée !",
        description: `${getDisplayName(selectedRecipient)} recevra votre suggestion`
      });

      onClose();
      setSelectedRecipient(null);
      setSearchTerm('');
      setMessage('');
    } catch (error: any) {
      console.error('Error suggesting profile:', error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📤 Proposer {getDisplayName(suggestedMember)} à quelqu'un</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profil suggéré */}
          <div className="p-4 border rounded-lg bg-muted/30">
            <div className="flex items-start gap-4">
              <img 
                src={suggestedMember.image || '/placeholder.svg'} 
                alt={getDisplayName(suggestedMember)}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="flex-1 space-y-2">
                <div>
                  <h3 className="font-semibold">{getDisplayName(suggestedMember)}</h3>
                  <p className="text-sm text-muted-foreground">
                    {suggestedMember.role && suggestedMember.company 
                      ? `${suggestedMember.role} • ${suggestedMember.company}`
                      : suggestedMember.role || suggestedMember.company || ''}
                  </p>
                </div>

                {/* Recherche */}
                {suggestedMember.needs && suggestedMember.needs.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                      <Search className="h-3 w-3" />
                      <span>Recherche</span>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {suggestedMember.needs.map((need, idx) => (
                        <Badge key={idx} variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                          {need}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Propose */}
                {suggestedMember.offers && suggestedMember.offers.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                      <Gift className="h-3 w-3" />
                      <span>Propose</span>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {suggestedMember.offers.map((offer, idx) => (
                        <Badge key={idx} variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                          {offer}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sélection du destinataire */}
          <div className="space-y-2">
            <label className="text-sm font-medium">À qui voulez-vous suggérer ce profil ?</label>
            <Input
              type="text"
              placeholder="Rechercher un membre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            {searchTerm && filteredMembers.length > 0 && (
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {filteredMembers.map(member => (
                  <button
                    key={member.id}
                    onClick={() => {
                      setSelectedRecipient(member);
                      setSearchTerm('');
                    }}
                    className={`w-full p-3 text-left hover:bg-muted/50 transition flex items-center gap-3 ${
                      selectedRecipient?.id === member.id ? 'bg-primary/10' : ''
                    }`}
                  >
                    <img 
                      src={member.image || '/placeholder.svg'} 
                      alt={getDisplayName(member)}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium">{getDisplayName(member)}</p>
                      <p className="text-xs text-muted-foreground">{member.company}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {selectedRecipient && (
              <div className="p-3 border rounded-md bg-primary/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src={selectedRecipient.image || '/placeholder.svg'} 
                    alt={getDisplayName(selectedRecipient)}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium">{getDisplayName(selectedRecipient)}</p>
                    <p className="text-xs text-muted-foreground">{selectedRecipient.company}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedRecipient(null)}
                >
                  Changer
                </Button>
              </div>
            )}
          </div>

          {/* Message personnalisé */}
          {selectedRecipient && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Message (modifiable)</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                placeholder="Votre message..."
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Annuler
            </Button>
            <Button onClick={handleSuggest} disabled={isLoading || !selectedRecipient}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer la suggestion
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
